import React from 'react';
import { 
  TrendingUp, TrendingDown, Minus, ArrowRight,
  Users, Server, ShoppingCart, Wrench, DollarSign,
  CheckCircle, AlertTriangle, Clock, XCircle
} from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'increase' | 'decrease' | 'neutral';
  icon: React.ComponentType<any>;
  color: string;
  onClick?: () => void;
}

interface QuickActionProps {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  onClick: () => void;
}

interface StatusCardProps {
  title: string;
  items: {
    label: string;
    value: number;
    status: 'success' | 'warning' | 'error' | 'info';
  }[];
}

export const MobileStatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  changeType,
  icon: IconComponent,
  color,
  onClick
}) => {
  const getChangeIcon = () => {
    switch (changeType) {
      case 'increase': return TrendingUp;
      case 'decrease': return TrendingDown;
      default: return Minus;
    }
  };

  const getChangeColor = () => {
    switch (changeType) {
      case 'increase': return 'text-green-600';
      case 'decrease': return 'text-red-600';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const ChangeIcon = getChangeIcon();

  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 ${
        onClick ? 'cursor-pointer hover:shadow-md active:scale-95' : ''
      } transition-all duration-200`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 ${color} rounded-lg flex items-center justify-center`}>
          <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
        {onClick && <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />}
      </div>
      
      <div>
        <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 line-clamp-2">{title}</p>
        <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{value}</p>
        
        {change && (
          <div className="flex items-center">
            <ChangeIcon className={`w-3 h-3 mr-1 ${getChangeColor()}`} />
            <span className={`text-xs font-medium ${getChangeColor()}`}>
              {change}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const MobileQuickAction: React.FC<QuickActionProps> = ({
  title,
  description,
  icon: IconComponent,
  color,
  onClick
}) => {
  return (
    <button
      onClick={onClick}
      className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-left hover:shadow-md active:scale-95 transition-all duration-200"
    >
      <div className="flex items-center">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 ${color} rounded-lg flex items-center justify-center mr-3 sm:mr-4`}>
          <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 text-sm sm:text-base truncate">{title}</h3>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{description}</p>
        </div>
        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
      </div>
    </button>
  );
};

export const MobileStatusCard: React.FC<StatusCardProps> = ({ title, items }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'error': return XCircle;
      default: return Clock;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-primary';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      
      <div className="space-y-3">
        {items.map((item, index) => {
          const StatusIcon = getStatusIcon(item.status);
          return (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center">
                <StatusIcon className={`w-4 h-4 mr-2 ${getStatusColor(item.status)}`} />
                <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
              </div>
              <span className={`text-sm font-semibold ${getStatusColor(item.status)}`}>
                {item.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Mobile-optimized grid layouts
export const MobileGrid: React.FC<{ children: React.ReactNode; cols?: 1 | 2 }> = ({ 
  children, 
  cols = 2 
}) => {
  return (
    <div className={`grid ${cols === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-3 sm:gap-4`}>
      {children}
    </div>
  );
};

export const MobileSection: React.FC<{ 
  title: string; 
  children: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
}> = ({ title, children, action }) => {
  return (
    <div className="mb-4 sm:mb-6">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {action && (
          <button
            onClick={action.onClick}
            className="text-xs sm:text-sm text-primary hover:text-primary-dark font-medium"
          >
            {action.label}
          </button>
        )}
      </div>
      {children}
    </div>
  );
};